import type { Contract, ContractLine } from "@/lib/types"

/**
 * MRR: el ingreso recurrente que esta corriendo AHORA.
 *
 * DOS FILTROS, Y NINGUNO ES OBVIO
 * --------------------------------
 * **1. Se suma por LINEA, no por contrato.** Antes se sumaba el importe del
 * contrato cuando su PRIMERA linea era recurrente. Con eso, un contrato mixto
 * —una cuota mensual mas un hito de implantacion— metia el hito entero dentro
 * del MRR, y uno cuya primera linea fuera el hito no aportaba su cuota.
 *
 * **2. Solo cuentan las lineas VIGENTES hoy.** Se sumaban todas las lineas
 * recurrentes de todo contrato marcado `activo`, sin mirar sus fechas. Medido
 * sobre los datos reales: de USD 54.865 de MRR, **USD 18.660 —el 34%— venian de
 * ocho lineas que ya habian terminado**, una de ellas en octubre de 2025.
 *
 * MRR significa ingreso recurrente AHORA. Una linea que termino el año pasado
 * no es recurrente: es historia. Y el `estado` del contrato no alcanza para
 * filtrarla, porque esos ocho contratos siguen marcados activos con la vigencia
 * vencida — eso es dato viejo del maestro y se reporta aparte.
 *
 * POR QUE SE DEVUELVE TAMBIEN LO QUE QUEDA FUERA
 * -----------------------------------------------
 * Porque un indicador que baja un 34% de un dia para otro se lee como un error
 * del sistema, no como una correccion. Mostrar lo excluido convierte la caida en
 * una explicacion.
 */
export interface Mrr {
  /** Lo que de verdad recurre hoy. */
  vigente: number
  /** Lineas recurrentes que ya terminaron o todavia no empiezan. */
  noVigente: number
  /** Contratos activos con la vigencia vencida: dato viejo, no calculo. */
  vencidosActivos: number
}

/** ¿Esta linea esta corriendo en `hoy`? Sin fechas, se asume que si. */
export function lineaVigente(linea: ContractLine, hoy: string): boolean {
  if (linea.startDate && linea.startDate > hoy) return false
  if (linea.endDate && linea.endDate < hoy) return false
  return true
}

export function calcularMrr(contratos: Contract[], hoy: string): Mrr {
  const activos = contratos.filter((c) => c.status === "activo")

  const recurrentes = activos
    .flatMap((c) => c.lines ?? [])
    .filter((l) => l.billingModel === "fixed_recurring")

  const suma = (ls: ContractLine[]) =>
    ls.reduce((total, l) => total + l.amount * l.quantity, 0)

  return {
    vigente: suma(recurrentes.filter((l) => lineaVigente(l, hoy))),
    noVigente: suma(recurrentes.filter((l) => !lineaVigente(l, hoy))),
    vencidosActivos: activos.filter((c) => c.endDate && c.endDate < hoy).length,
  }
}
